/**
 * Signing in without leaving the page: the shared `LoginModal`
 * (@sudobility/components), mounted once and wired to this app's auth through
 * `useSignInProps`.
 *
 * Use `useSignIn().openSignIn()` wherever sign-in is needed in the middle of
 * something else — the top bar's "Sign In", the Credits and History pages'
 * signed-out prompts — so that on success the person is still where they
 * were, now signed in. The login route (`/:lang/login`, `LoginPage`) is only
 * for somebody who navigated there in order to sign in; nothing redirects to it.
 */

import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { LoginModal } from '@sudobility/components';
import { useSignInProps } from '../hooks/useSignInProps';
import { SignInContext, type SignInContextValue } from './sign-in-context';

export function SignInProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const pendingSuccess = useRef<(() => void) | undefined>(undefined);
  const { onEmailSignIn, onEmailSignUp, onPasswordReset, onGoogleSignIn, text, modalText } =
    useSignInProps();

  const openSignIn = useCallback((onSuccess?: () => void) => {
    pendingSuccess.current = onSuccess;
    setOpen(true);
  }, []);

  // The modal calls onSuccess and then onClose; a close on its own (button,
  // backdrop, Escape) just drops whatever was waiting on the sign-in.
  const handleClose = useCallback(() => {
    pendingSuccess.current = undefined;
    setOpen(false);
  }, []);
  const handleSuccess = useCallback(() => {
    pendingSuccess.current?.();
  }, []);

  const value = useMemo<SignInContextValue>(() => ({ openSignIn }), [openSignIn]);

  return (
    <SignInContext.Provider value={value}>
      {children}
      <LoginModal
        open={open}
        onClose={handleClose}
        onSuccess={handleSuccess}
        onEmailSignIn={onEmailSignIn}
        onEmailSignUp={onEmailSignUp}
        onPasswordReset={onPasswordReset}
        onGoogleSignIn={onGoogleSignIn}
        text={text}
        modalText={modalText}
      />
    </SignInContext.Provider>
  );
}
